import { useMemo } from "react";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import { DragTypes } from "../../models/DragTypes";
import {
  DropOffSort,
  IDropOffDragItem,
  IDropOffLeadRow,
} from "../../models/DropOffPlanner";
import classNames from "../../utils/classNames";
import LoadingPulse from "../shared/LoadingPulse";
import DropOffLeadCard from "./DropOffLeadCard";

interface UnscheduledDropOffsPanelProps {
  leads: IDropOffLeadRow[];
  isLoading: boolean;
  searchTerm: string;
  sort: DropOffSort;
  onDropFromCalendar: (item: IDropOffDragItem) => void;
}

export default function UnscheduledDropOffsPanel({
  leads,
  isLoading,
  searchTerm,
  sort,
  onDropFromCalendar,
}: UnscheduledDropOffsPanelProps) {
  const { t } = useTranslation("leads");
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DragTypes.DROP_OFF_LEAD,
      drop: (item: IDropOffDragItem) => {
        if (item.currentScheduledOn) {
          onDropFromCalendar(item);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [onDropFromCalendar]
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const list = leads.filter((l) => {
      if (term.length === 0) return true;
      const haystack = [l.vrm, l.combinedMake, l.combinedModel, l.source, l.postcode]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });

    return list.sort((a, b) => {
      const aT = new Date(a.created).getTime();
      const bT = new Date(b.created).getTime();
      return sort === "Oldest" ? aT - bT : bT - aT;
    });
  }, [leads, searchTerm, sort]);

  return (
    <aside
      ref={drop}
      className={classNames(
        "bg-white border border-gray-200 rounded-md flex flex-col h-full overflow-hidden",
        isOver ? "ring-2 ring-primary-400" : ""
      )}
    >
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">{t("Unscheduled Drop Offs")}</span>
        <span className="text-xs text-gray-500">({filtered.length})</span>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {isLoading ? (
          <LoadingPulse />
        ) : filtered.length === 0 ? (
          <div className="text-center text-xs text-gray-400 mt-6 px-4">
            {t("No unscheduled drop offs.")}
          </div>
        ) : (
          filtered.map((l) => <DropOffLeadCard key={l.id} lead={l} />)
        )}
      </div>
    </aside>
  );
}
